import {getSessionCookie} from './cookie-utility';
import DPNetworkError from './error/DPNetworkError';
import {storeSessionData} from "./global-util";


export const SESSION_ID_PARAM_KEY = 'sessionId';

/**
 * Appends query params to a path.
 *
 * Based on: https://github.com/github/fetch/issues/256#issuecomment-304198513
 * @param {string} path A URL path.
 * @param {object} params A simple object with {key, value} pairs that represent query params.
 */
export function createPathWithParams(path, params) {
  if (!params || Object.keys(params).length === 0) return path;

  const urlParams = new URLSearchParams(Object.entries(params));
  return `${path}?${urlParams}`;
}

/**
 * Checks if a url is relative or absolute.
 *
 * Taken from: https://stackoverflow.com/a/38979205/4480526
 * @param {string} url The url to be checked.
 */
export function isUrlAbsolute(url) {
  if (url.indexOf('//') === 0) return true; // URL is protocol-relative (= absolute)
  if (url.indexOf('://') === -1) return false; // URL has no protocol (= relative)
  if (url.indexOf('.') === -1) return false; // URL does not contain a dot, i.e. no TLD (= relative, possibly REST)
  if (url.indexOf('/') === -1) return false; // URL does not contain a single slash (= relative)
  if (url.indexOf(':') > url.indexOf('/')) return false; // The first colon comes after the first slash (= relative)
  if (url.indexOf('://') < url.indexOf('.')) return true; // Protocol is defined before first dot (= absolute)
  return false; // Anything else must be relative
}

/**
 * Converts a relative portal url to include the base portal path.
 * (PUBLIC_HTML set in build file for the driver portal or (future) customer portal and so on)
 * (PUBLIC_HTML not set in development (localhost), PORT can be set to differentiate localhost servers)
 * @param {string} location The relative url.
 */
export const asPortalUrl = location => `${process.env.hasOwnProperty('PUBLIC_URL') ? process.env.PUBLIC_URL : ''}${location}`;

/**
 * Converts a relative API url to include the API URL or path.
 * (REACT_APP_API_URL not set in development (localhost) - uses proxy to get to dev server)
 * @param {string} location The relative url.
 */
export const asAPIUrl = location => `${process.env.hasOwnProperty('REACT_APP_API_URL') ? process.env.REACT_APP_API_URL : ''}${location}`;

/**
 * Converts a relative API url to include the API URL or path and the session cookie.
 * @param {string} location The relative url.
 */
export const asAPIUrlWithSession = location => `${asAPIUrl(location)}?${SESSION_ID_PARAM_KEY}=${getSessionCookie()}`;

/**
 * Handles the response body of a DP response.
 * @param {object} response The fetch response from a DP API
 */
function handlePortalResponseBody(response) {
  // 204 (No Content) will never have a body.
  if (response.status === 204) return new Promise(resolve => resolve());

  // 404 (Not Found) gives an HTML page. We can ignore this in the context of fetching data.
  if (response.status === 404) return new Promise(resolve => resolve());

  // Log response if not good
  if (!response.ok) {
    console.log('handlePortalResponseBody response not ok - response obj: ');
    console.log(response);
  }

  // Check the content type.
  const contentType = response.headers.get('content-type');
  if (!contentType) return new Promise(resolve => resolve());

  // Check the content length. This may not be included, even when there is content.
  let contentLength = response.headers.get('content-length');
  contentLength = contentLength ? Number(contentLength) : null;
  if (contentLength != null && contentLength === 0) return new Promise(resolve => resolve());

  // Handle the different types of formats. Will need to be handled case by case.
  if (contentType.startsWith('image/svg+xml')) return response.text();
  if (contentType.startsWith('text/plain')) return response.text();
  if (contentType.startsWith('text/html')) return response.text();
  if (contentType.startsWith('application/json')) return response.json();
  if (contentType.startsWith('application/octet-stream')) return response.blob();
  if (contentType.startsWith('application/pdf')) return response.blob();

  // Content type hasn't been handled. Warn the developer with log and message text...
  console.log(`Portal fetch response: Unknown content type: ${contentType}`); // eslint-disable-line no-console
  return `Unknown content type: ${contentType}`;
}

export async function request(method, location, queryParams, body, progressPercentFunction) {
  return requestWithHeaders(method, location, queryParams, body, progressPercentFunction, null);
}
/**
 * @param {string} method The request method for the http call. see: https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
 * @param {location} location The location for the http call.
 * @param {object} [queryParams] (optional) Additional query parameters: { key1: value1, key2: ... }
 * @param {object} [body] (optional) Additional body of the request.
 * @param {function} progressPercentFunction (optional) Provide if wish to update progress or loading indicators.
 * @returns {promise} A promise with the response data or an error.
 */
export async function requestWithHeaders(method, location, queryParams, body, progressPercentFunction, headers) {

  try {
    //console.log('>> API call: ' + method + ' ' + location + (queryParams ? ('' + queryParams) : ''));

    // Progress & param's
    if (progressPercentFunction) {
      progressPercentFunction(0);
    }
    const params = queryParams || {};

    // We always pass up the session-id as a query parameter to prevent CSRF.
    if (!headers)
    {
      params[SESSION_ID_PARAM_KEY] = getSessionCookie();
    }
    // Add params to the path. If the path is not absolute then prefix it with our own base path.
    const path = createPathWithParams(isUrlAbsolute(location) ? location : asAPIUrl(location), params);

    const requestOptions = {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: headers? {
        ...headers,
        'content-type': 'application/json',
      } : {
        'content-type': 'application/json',
      },
    };

    storeSessionData('lastFetchPath', location + (queryParams ? ('' + queryParams) : '')); //used to try give a helpful error in error-handler.js - may not be accurate if multiple errors at once (delay in processing errors, location may be last one for all)
    storeSessionData('lastFetchMethod', method);
    const response = await fetch(path, requestOptions);

    const responseData = handlePortalResponseBody(response);
    if (!response.ok) {
      const errorData = await responseData;
      let errorMessage = '';
      if (errorData) {
        if (errorData.hasOwnProperty('errorMessage')) {
          errorMessage = errorData.errorMessage;
        } else {
          errorMessage = '' + errorData;
        }
      }
      if (progressPercentFunction) { progressPercentFunction(100); }
      throw new DPNetworkError(errorMessage, response.status, errorData);
    }
    if (progressPercentFunction) { progressPercentFunction(100); }
    return responseData;
  } catch (error) {
    if (progressPercentFunction) { progressPercentFunction(100); }
    throw error;
  }
}


/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {object} [body] The body of the request in JSON format.
 * @param {function} progressPercentFunction (optional) Provide if wish to update progress or loading indicators.
 * @example
 * post('/api/example/1', { param1: 'value1', param2: 'value2' }, { description: 'example' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 */
export function post(url, params, body, progressPercentFunction) {
  return request('POST', url, params, body, progressPercentFunction);
}

export function postWithHeaders(url, params, body, progressPercentFunction, headers) {
  return requestWithHeaders('POST', url, params, body, progressPercentFunction, headers);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the sessionId as a cookie and query parameter.
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {object} [body] The body of the request in JSON format.
 * @param {function} progressPercentFunction (optional) Provide if wish to update progress or loading indicators.
 * @example
 * put('/api/example/1', { param1: 'value1', param2: 'value2' }, { description: 'example' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 */
export function put(url, params, body, progressPercentFunction) {
  return request('PUT', url, params, body, progressPercentFunction);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @example
 * get('/api/example', { param1: 'value1', param2: 'value2' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {function} progressPercentFunction (optional) Provide if wish to update progress or loading indicators.
 */
export function remove(url, params, progressPercentFunction) {
  return request('DELETE', url, params, null, progressPercentFunction);
}

/**
 * Creates an HTTP post and returns a promise.
 * This automatically sends up the session-id as a cookie and query parameter.
 *
 * @example
 * get('/api/example', { param1: 'value1', param2: 'value2' })
 *  .then(response => { console.log(`success: ${response}`) })
 *  .catch(error => { console.log(`failed: ${error}` )});
 *
 * @param {string} url The url of the request.
 * @param {object} [params] (optional) Additional query parameters: { key1: value1, key2: value2 }
 * @param {function} progressPercentFunction (optional) Provide if wish to update progress or loading indicators.
 */
export function get(url, params, progressPercentFunction) {
  return request('GET', url, params, null, progressPercentFunction);
}

/**
 * External Get (user not logged in)
 * @param {string} url The location for the http call.
 * @param {object} [queryParams] (optional) Additional query parameters: { key1: value1, key2: ... }
 * @param {object} [body] (optional) Additional body of the request.
 * @returns {promise} A promise with the response data or an error.
 */
export async function externalGet(url, queryParams, body) {
  const params = queryParams || {};

  // Add params to the path. If the path is not absolute then prefix it with our own base path.
  const path = createPathWithParams(isUrlAbsolute(url) ? url : asAPIUrl(url), params);

  const requestOptions = {
    method: 'GET',
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
  };

  const response = await fetch(path, requestOptions);
  const responseData = handlePortalResponseBody(response);
  if (!response.ok) {
    const errorData = await responseData;
    let errorMessage = '';
    if (errorData) {
      if (errorData.hasOwnProperty('errorMessage')) {
        errorMessage = errorData.errorMessage;
      } else {
        errorMessage = '' + errorData;
      }
    }
    throw new DPNetworkError(errorMessage, response.status, errorData);
  }
  return responseData;
}
